import React, { useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import DatePicker from 'react-datepicker';
import { InputLabel, InputError } from 'src/domains/layouts/shared/formComponents/FormComponents.style';
import { FormComponent, FormWrapper, DatesWrapper, DateInputWrapper } from 'src/domains/layouts/shared/cms/form/Form.style';
import { RedirectOptions } from './RedirectOptions';
import { DateState } from './Date.state';
import { CasinoBannersDataState } from 'src/AppState/CmsState/CasinoBannersDataState/CasinoBannersData.state';
import { Select } from 'src/domains/layouts/shared/formComponents/Select/components/Select';
import { UploadImg } from 'src/components/UploadImg/UploadImg';
import { Input } from 'src/domains/layouts/shared/formComponents/input/Input';
import { Textarea } from 'src/domains/layouts/shared/formComponents/textarea/Textarea';
import { FormHeader } from 'src/domains/layouts/shared/cms/form/formHeader/FormHeader';
import { FormButtons } from 'src/domains/layouts/shared/buttons/FormButtons';
import { Loader } from 'src/domains/layouts/shared/loader/Loader';

interface PropsTypes {
    casinoBannersData: CasinoBannersDataState,
}

export const CasinoBannersForm = observer('CasinoBannersForm', ({ casinoBannersData }: PropsTypes) => {
    const [state] = useState(() => new DateState(casinoBannersData));
    const { onChangeStartDate, onBlurStartDate, onChangeEndDate, onBlurEndDate, startDateError, endDateError } = state;

    const {
        title,
        termsHeadline,
        termsFull,
        redirectType,
        imageDesktop,
        imageTablet,
        imageMobile,
        redirectOptions,
        handleCreate,
        handleResetForm,
        handleEdit,
        isEditMode,
        isLoading,
        pageName,
        resetForm,
        startDate,
        endDate,
        imagesError,
    } = casinoBannersData;

    return (
        <FormWrapper>
            <FormHeader isEditMode={isEditMode} defaultLabel={`Create new ${pageName} banner`} editModeLabel={`Edit ${pageName} banner`} resetForm={resetForm}/>

            <FormComponent onSubmit={(e: React.FormEvent<HTMLFormElement>): void => {
                e.preventDefault();
            }}>
                <Loader isActive={isLoading}/>

                <Input
                    label='Title'
                    formInputState={title}
                    dataTest='banner-title'
                />

                <DatesWrapper>
                    <DateInputWrapper>
                        <InputLabel>Start date *</InputLabel>
                        <DatePicker
                            selected={startDate}
                            onChange={onChangeStartDate}
                            onBlur={onBlurStartDate}
                            showTimeSelect
                            timeFormat='HH:mm'
                            timeIntervals={15}
                            timeCaption='time'
                            dateFormat='MMMM d, yyyy h:mm aa'
                            id='banner-start-date'
                        />
                        {startDateError ? <InputError>This field is required.</InputError> : null}
                    </DateInputWrapper>
                    <DateInputWrapper>
                        <InputLabel>End date *</InputLabel>
                        <DatePicker
                            selected={endDate}
                            onChange={onChangeEndDate}
                            onBlur={onBlurEndDate}
                            showTimeSelect
                            timeFormat='HH:mm'
                            timeIntervals={15}
                            timeCaption='time'
                            dateFormat='MMMM d, yyyy h:mm aa'
                            id='banner-end-date'
                        />
                        {endDateError ? <InputError>This field is required.</InputError> : null}
                    </DateInputWrapper>
                </DatesWrapper>

                <Select defaultText='Select redirection' selectOptions={redirectOptions} stateValue={redirectType} dataTest='banner-redirection-select' />
                <RedirectOptions casinoBannersData={casinoBannersData} />

                <Input
                    label='Headline terms'
                    formInputState={termsHeadline}
                    dataTest='banner-headline-terms'
                />

                <Textarea 
                    label='Full terms'
                    placeholder='Type something...'
                    formState={termsFull}
                    ckeditor
                    dataTest='banner-full-terms'
                />

                <>
                    <UploadImg
                        title='Image Upload Desktop (1807 x 286px)'
                        uploadImageState={imageDesktop}
                        hasFullWidthPreview={true}
                        dataTest='banner-desktop-image'
                    />
                    <UploadImg
                        title='Image Upload Tablet (768 x 188px)'
                        uploadImageState={imageTablet}
                        hasFullWidthPreview={true}
                        dataTest='banner-tablet-image'
                    />
                    <UploadImg
                        title='Image Upload Mobile (343 x 120px)'
                        uploadImageState={imageMobile}
                        hasFullWidthPreview={true}
                        dataTest='banner-mobile-image'
                    />
                    {imagesError !== null ? <InputError>{imagesError}</InputError> : null}
                </>

                <FormButtons
                    onCreate={handleCreate}
                    onUpdate={handleEdit}
                    onCancel={handleResetForm}
                    isEditMode={isEditMode}
                    dataTestCancel='banner-cancel-button'
                    dataTestCreate='banner-create-button'
                    dataTestUpdate='banner-update-button'
                />
            </FormComponent>
        </FormWrapper>
    );
});

